/* eslint-disable @typescript-eslint/no-unused-vars */
import useBodyScrollLock from '@hooks/useBodyScrollLock';
import useOneTrust, { OneTrustStatus } from '@hooks/useOneTrust';
import Cross from '@img/icons/icon-cross-black.svg';
import Button from '@stories/Elements/Forms/Button/Button';
import { ButtonIcon, ButtonText } from '@stories/Elements/Forms/Button/Button.styles';
import Loader from '@stories/Elements/Loaders/Loader/Loader';
import * as React from 'react';
import { createGlobalStyle } from 'styled-components';
import S from './CookiePopup.styles';
const OneTrustCSSOverrides = createGlobalStyle `
  #onetrust-banner-sdk, .onetrust-pc-dark-filter {
    display: none !important;
  }
`;
const POLLING_INTERVAL = 100;
const CookiePopup = (props) => {
    const [isOpen, setIsOpen] = React.useState(!props.oneTrustPopupClosed);
    const [readMore, setReadMore] = React.useState(false);
    const scrollableRef = useBodyScrollLock(isOpen);
    const buttonSize = props.onlyUseNecessaryCookiesText ? 'small' : 'default';
    const { status, loadOneTrust, logError } = useOneTrust({
        oneTrustScriptID: props.oneTrustScriptID,
        cultureCode: props.pageContext.documentCulture,
    });
    const clearButtonStates = () => {
        const acceptAllButton = document.querySelector('#cookie-onetrust-accept-all');
        const showInfoButton = document.querySelector('#cookie-onetrust-show-info');
        const acceptNecessaryButton = document.querySelector('#cookie-onetrust-only-necessary');
        if (acceptAllButton) {
            const iconElement = acceptAllButton.querySelector('i');
            if (iconElement !== null) {
                iconElement.style.display = 'none';
            }
            acceptAllButton.setAttribute('data-progress', 'false');
            acceptAllButton.setAttribute('data-icon-padding', 'false');
        }
        if (showInfoButton) {
            const iconElement = showInfoButton.querySelector('i');
            if (iconElement !== null) {
                iconElement.style.display = 'none';
            }
            showInfoButton.setAttribute('data-progress', 'false');
            showInfoButton.setAttribute('data-icon-padding', 'false');
        }
        if (acceptNecessaryButton) {
            const iconElement = acceptNecessaryButton.querySelector('i');
            if (iconElement !== null) {
                iconElement.style.display = 'none';
            }
            acceptNecessaryButton.setAttribute('data-progress', 'false');
            acceptNecessaryButton.setAttribute('data-icon-padding', 'false');
        }
    };
    const ShowInfoDisplay = async () => {
        const oneTrust = await loadOneTrust();
        try {
            oneTrust?.ToggleInfoDisplay();
            clearButtonStates();
        }
        catch (e) {
            await logError(e.message, OneTrustStatus.Errored);
        }
    };
    const AcceptAll = async () => {
        const oneTrust = await loadOneTrust();
        try {
            oneTrust?.AllowAll();
            clearButtonStates();
        }
        catch (e) {
            await logError(e.message, OneTrustStatus.Errored);
        }
    };
    const AcceptNecessary = async () => {
        const oneTrust = await loadOneTrust();
        try {
            oneTrust?.Close();
            clearButtonStates();
        }
        catch (e) {
            await logError(e.message, OneTrustStatus.AcceptNecessaryError);
        }
    };
    const processUnhandledEvents = () => {
        if (!window.OneTrustUnhandledEvent || window.OneTrustUnhandledEvent.length === 0) {
            return;
        }
        // Handle the unhandled event which may be set before react hydrates
        switch (window.OneTrustUnhandledEvent) {
            case 'show-info': {
                ShowInfoDisplay();
                break;
            }
            case 'accept-all': {
                AcceptAll();
                break;
            }
            case 'only-necessary': {
                AcceptNecessary();
                break;
            }
        }
        delete window.OneTrustUnhandledEvent;
    };
    React.useEffect(() => {
        // Handle closing our modal when the one trust has been closed / accepted
        const handle = window.setInterval(async () => {
            if (window.OneTrust && window.OneTrust.IsAlertBoxClosed !== undefined && window.OneTrust.IsAlertBoxClosed()) {
                setIsOpen(false);
                window.clearInterval(handle);
            }
        }, POLLING_INTERVAL);
    }, []);
    // Load onetrust on pageload if the cookie has already been accepted
    React.useEffect(() => {
        if (!props.oneTrustPopupClosed) {
            return;
        }
        loadOneTrust();
    }, []);
    // Load onetrust on load if the buttons have been clicked prior to react loading
    React.useEffect(() => {
        processUnhandledEvents();
    }, []);
    React.useEffect(() => {
        async function handleStatus() {
            if (status === OneTrustStatus.ErroredLoading || status === OneTrustStatus.AcceptNecessaryError) {
                console.debug('Onetrust errored, hydrating anyway');
                window.dispatchEvent(new Event('hydrate'));
                setIsOpen(false);
            }
            else if (status === OneTrustStatus.Errored || status === OneTrustStatus.LoadedMissingSchema || status === OneTrustStatus.NoScriptId) {
                // Try atleast accept necessary cookies
                console.debug('Onetrust errored, attempting to accept necessary cookies');
                await AcceptNecessary();
                setIsOpen(false);
            }
        }
        handleStatus();
    }, [status]);
    if (!isOpen ||
        status === OneTrustStatus.Errored ||
        status === OneTrustStatus.LoadedMissingSchema ||
        status === OneTrustStatus.NoScriptId ||
        status === OneTrustStatus.ErroredLoading) {
        return null;
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(OneTrustCSSOverrides, null),
        React.createElement(S.Container, null,
            React.createElement(S.Modal, { ref: scrollableRef, onMouseEnter: loadOneTrust, "data-buttons": props.onlyUseNecessaryCookiesText ? 3 : 2 },
                props.showClose ? (React.createElement(S.Close, { id: "cookie-onetrust-close", onClick: AcceptNecessary },
                    React.createElement(Cross, null))) : null,
                React.createElement(S.ModalInner, null,
                    React.createElement(S.Title, null, props.title),
                    React.createElement(S.Description, { dangerouslySetInnerHTML: { __html: props.description } }),
                    props.extendedDescription && props.readMoreButtonText && props.readLessButtonText ? (React.createElement(React.Fragment, null,
                        readMore ? React.createElement(S.Description, { dangerouslySetInnerHTML: { __html: props.extendedDescription } }) : null,
                        React.createElement(S.ReadMore, { onClick: () => setReadMore(!readMore) }, readMore === false ? props.readMoreButtonText : props.readLessButtonText))) : null,
                    React.createElement(S.Buttons, null,
                        React.createElement(Button, { id: "cookie-onetrust-show-info", icon: "none", theme: "grey", onClick: ShowInfoDisplay, iconPosition: "after", size: buttonSize },
                            React.createElement(ButtonText, null, props.openCookieSettingsText),
                            React.createElement(ButtonIcon, { position: "after", size: "default", style: { display: 'none' } },
                                React.createElement(Loader, { inline: true, width: 2, size: 18 }))),
                        props.onlyUseNecessaryCookiesText ? (React.createElement(Button, { id: "cookie-onetrust-only-necessary", theme: "white", icon: "none", onClick: AcceptNecessary, iconPosition: "after", size: buttonSize },
                            React.createElement(ButtonText, null, props.onlyUseNecessaryCookiesText),
                            React.createElement(ButtonIcon, { position: "after", size: "default", style: { display: 'none' } },
                                React.createElement(Loader, { inline: true, width: 2, size: 18 })))) : null,
                        React.createElement(Button, { id: "cookie-onetrust-accept-all", theme: "green", icon: "none", onClick: AcceptAll, iconPosition: "after", size: buttonSize },
                            React.createElement(ButtonText, null, props.acceptAllCookiesText),
                            React.createElement(ButtonIcon, { position: "after", size: "default", style: { display: 'none' } },
                                React.createElement(Loader, { inline: true, width: 2, size: 18 }))),
                        React.createElement("script", { dangerouslySetInnerHTML: {
                                __html: `
                (() => {
                  const acceptAllButton = document.querySelector('#cookie-onetrust-accept-all');
                  const showInfoButton = document.querySelector('#cookie-onetrust-show-info');
                  const acceptNecessaryButton = document.querySelector('#cookie-onetrust-only-necessary');
                  const closeButton = document.querySelector('#cookie-onetrust-close');

                  function addClickToButton(button, id, dataAttributes) {
                    if (button === null) {
                      return;
                    }

                    button.addEventListener('click', (e) => handleClick(e, id, dataAttributes));
                  }

                  function clearButton(button) {
                    if (button === null) {
                      return;
                    }

                    const iconElement = button.querySelector('i');

                    if (iconElement !== null) {
                      iconElement.style.display = 'none';
                    }

                    button.setAttribute('data-progress', 'false');
                    button.setAttribute('data-icon-padding', 'false');
                  }

                  function handleClick(event, id, dataAttributes) {
                    clearButton(acceptAllButton);
                    clearButton(showInfoButton);
                    clearButton(acceptNecessaryButton);

                    // Show i icon
                    const iconElement = event.currentTarget.querySelector('i');

                    if (iconElement !== null) {
                      iconElement.style.display = '';
                    }


                    if (dataAttributes) {
                      event.currentTarget.setAttribute('data-icon-padding', 'true');
                      event.currentTarget.setAttribute('data-icon-position', 'after');
                      event.currentTarget.setAttribute('data-size', '${buttonSize}');
                      event.currentTarget.setAttribute('data-progress', 'true');
                    }

                    window.OneTrustUnhandledEvent = id;
                  }

                  addClickToButton(acceptAllButton, 'accept-all', true);
                  addClickToButton(showInfoButton, 'show-info', true);
                  addClickToButton(acceptNecessaryButton, 'only-necessary', true);
                  addClickToButton(closeButton, 'only-necessary', false);
                })();
            `,
                            } })))))));
};
export default CookiePopup;
